/*
 * <dwm-growth-loop> — Groeipartner cycle.
 *
 * Deliberately simple:
 *   - five explicit states, defined in the section (JSON in [data-growth-states]):
 *       { step, channels: [channel block ids], labels: [channel labels], focusText }
 *   - one activeIndex (0 … states.length − 1), the only state there is;
 *   - one renderState(activeIndex) that renders everything from that index;
 *   - one autoplay timer (setTimeout, at most one pending per component).
 *
 * renderState() first resets every step and every channel chip, then sets only the ones of the active state,
 * updates the focus text and the ARIA attributes. Nothing reads old classes back. CSS transitions are visual only;
 * the light trail's fade is cancelled before a new one starts, so an old animation can never touch a new state.
 *
 * Autoplay runs only when motion is allowed and the section has `data-animate`, and only while the component is in
 * view, the tab is visible, it is not paused with the button and no step has keyboard focus. With reduced motion,
 * in the Theme Editor or with "Animaties" off there is no autoplay; clicking a step still works, instantly.
 */
import { inView } from 'vendor';
import { isMotionAllowed } from 'dwm-motion';

const STEP_DURATION = 1900;
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

class DwmGrowthLoop extends HTMLElement {
  #states = [];
  #activeIndex = 0;
  #timer = null;
  #autoplayAllowed = false;
  #inView = false;
  #paused = false;
  #keyboardFocus = false;
  #stopInView = null;
  #orbitAnimation = null;
  #steps = [];
  #buttons = [];
  #chips = [];
  #focus = null;
  #orbit = null;

  connectedCallback() {
    this.#teardown();

    try {
      this.#states = JSON.parse(this.querySelector('[data-growth-states]')?.textContent || '[]');
    } catch {
      this.#states = [];
    }

    this.#steps = [...this.querySelectorAll('[data-step]')];
    this.#buttons = [...this.querySelectorAll('[data-step-button]')];
    this.#chips = [...this.querySelectorAll('[data-channel]')];
    this.#focus = this.querySelector('[data-growth-focus]');
    this.#orbit = this.querySelector('.dwm-growth__orbit');

    if (this.#states.length < 2 || this.#states.length !== this.#steps.length) {
      return;
    }

    this.addEventListener('click', this.#onClick);
    this.addEventListener('focusin', this.#onFocusChange);
    this.addEventListener('focusout', this.#onFocusChange);
    this.addEventListener('dwm:pause-change', this.#onPauseChange);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    reducedMotionQuery.addEventListener('change', this.#onReducedMotionChange);

    this.#autoplayAllowed = this.hasAttribute('data-animate') && isMotionAllowed();

    if (this.#autoplayAllowed) {
      this.renderState(0);
      this.#stopInView = inView(this, () => {
        this.#inView = true;
        this.startAutoplay();

        return () => {
          this.#inView = false;
          this.stopAutoplay();
        };
      }, { amount: 0.35 });
    }
  }

  disconnectedCallback() {
    this.#teardown();
  }

  /* Removes every timer, animation and listener; also run before (re)connecting, so there is never a second timer. */
  #teardown() {
    this.stopAutoplay();
    this.#orbitAnimation?.cancel();
    this.#orbitAnimation = null;
    this.#stopInView?.();
    this.#stopInView = null;
    this.#inView = false;
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('focusin', this.#onFocusChange);
    this.removeEventListener('focusout', this.#onFocusChange);
    this.removeEventListener('dwm:pause-change', this.#onPauseChange);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    reducedMotionQuery.removeEventListener('change', this.#onReducedMotionChange);
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Rendering — the only place that touches the UI.
   * ------------------------------------------------------------------------------------------------------------ */

  renderState(index) {
    const state = this.#states[index];

    if (!state) {
      return;
    }

    this.#activeIndex = index;

    // 1. Reset every step.
    this.#steps.forEach((step) => step.classList.remove('is-active'));
    this.#buttons.forEach((button) => button.removeAttribute('aria-current'));

    // 2. Reset every channel chip.
    this.#chips.forEach((chip) => chip.classList.remove('is-selected'));

    // 3. Activate only the active step.
    this.#steps[index].classList.add('is-active');
    this.#buttons[index]?.setAttribute('aria-current', 'step');

    // 4. Activate only the channels of this state.
    this.#chips.forEach((chip) => {
      if (state.channels.includes(chip.dataset.channel)) {
        chip.classList.add('is-selected');
      }
    });

    // 5. Focus text, immediately.
    if (this.#focus) {
      this.#focus.textContent = state.focusText || ' ';
    }

    // 6. Visual indicators, derived from the index only.
    this.classList.add('is-running');
    this.classList.toggle('is-selecting', state.channels.length > 0);
    this.style.setProperty('--dwm-orbit-index', index);
    this.#fadeInOrbit();
  }

  /* The light trail jumps to the new step and fades in; any running fade is cancelled first. */
  #fadeInOrbit() {
    this.#orbitAnimation?.cancel();
    this.#orbitAnimation = null;

    if (this.#orbit && isMotionAllowed() && typeof this.#orbit.animate === 'function') {
      this.#orbitAnimation = this.#orbit.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 450,
        easing: 'cubic-bezier(.4, 0, .2, 1)',
      });
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Autoplay — one timer at most.
   * ------------------------------------------------------------------------------------------------------------ */

  get #canAutoplay() {
    return this.#autoplayAllowed && this.#inView && !this.#paused && !this.#keyboardFocus && !document.hidden;
  }

  startAutoplay() {
    this.scheduleNext();
  }

  stopAutoplay() {
    clearTimeout(this.#timer);
    this.#timer = null;
  }

  scheduleNext() {
    this.stopAutoplay();

    if (!this.#canAutoplay) {
      return;
    }

    this.#timer = setTimeout(() => {
      this.#timer = null;
      this.renderState((this.#activeIndex + 1) % this.#states.length);
      this.scheduleNext();
    }, STEP_DURATION);
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Events
   * ------------------------------------------------------------------------------------------------------------ */

  #onClick = (event) => {
    const button = event.target.closest('[data-step-button]');

    if (!button || !this.contains(button)) {
      return;
    }

    this.stopAutoplay();
    this.renderState(parseInt(button.dataset.stepButton, 10));
    this.startAutoplay();
  };

  /* Autoplay waits while a keyboard user is on one of the steps, so the highlight never moves away from focus. */
  #onFocusChange = () => {
    requestAnimationFrame(() => {
      const focused = Boolean(this.querySelector('[data-step-button]:focus-visible'));

      if (focused !== this.#keyboardFocus) {
        this.#keyboardFocus = focused;
        focused ? this.stopAutoplay() : this.startAutoplay();
      }
    });
  };

  #onPauseChange = (event) => {
    this.#paused = event.detail.paused;
    this.#paused ? this.stopAutoplay() : this.startAutoplay();
  };

  #onVisibilityChange = () => {
    document.hidden ? this.stopAutoplay() : this.startAutoplay();
  };

  #onReducedMotionChange = (event) => {
    if (event.matches) {
      this.#autoplayAllowed = false;
      this.stopAutoplay();
      this.#orbitAnimation?.cancel();
    }
  };
}

if (!customElements.get('dwm-growth-loop')) {
  customElements.define('dwm-growth-loop', DwmGrowthLoop);
}
